import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ChartTooltip, Legend } from 'recharts';
import { CostType, COST_CATEGORY_LABELS } from '../types/cost.types';
import costService from '../services/cost.service';

interface CostPieChartProps {
  costs: CostType[];
  totalCostsAmount: number;
}

const CostPieChart: React.FC<CostPieChartProps> = ({ costs, totalCostsAmount }) => {
  // Função para preparar os dados para o gráfico de pizza
  const preparePieChartData = () => {
    if (!costs || costs.length === 0 || totalCostsAmount <= 0) {
      return [];
    }
    
    // Ordenar os custos por valor (do maior para o menor)
    const sortedCosts = [...costs]
      .filter(cost => cost.amount > 0) // Filtrar apenas custos positivos
      .sort((a, b) => b.amount - a.amount);
    
    if (sortedCosts.length === 0) {
      return [];
    }
    
    // Pegar apenas os 3 maiores custos
    const topCosts = sortedCosts.slice(0, 3);
    
    // Preparar os dados para o gráfico
    const chartData = topCosts.map(cost => ({
      name: COST_CATEGORY_LABELS[cost.category] || cost.category,
      value: cost.amount,
      description: cost.description,
      percentage: (cost.amount / totalCostsAmount) * 100
    }));
    
    return chartData;
  };

  const chartData = preparePieChartData();
  
  if (chartData.length === 0) {
    return (
      <Paper elevation={3} sx={{ 
        p: 2, 
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        mt: 2
      }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom align="center">
          Distribuição dos Principais Custos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center', p: 2 }}>
          Não há custos com valores positivos para exibir no gráfico.
        </Typography>
      </Paper>
    );
  }
  
  return (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom align="center">
        Distribuição dos Principais Custos
      </Typography>
      <Box sx={{ height: 300, mt: 2, mb: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              // @ts-ignore - Propriedades do Recharts não estão corretamente tipadas
              labelLine={{
                // @ts-ignore
                offsetRadius: 10,
                stroke: '#999'
              }}
              outerRadius={80}
              innerRadius={30}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
              paddingAngle={4}
              // @ts-ignore - Propriedades do Recharts não estão corretamente tipadas
              label={{
                // @ts-ignore
                position: 'outside',
                offset: 10,
                fill: '#333',
                fontSize: 11,
                formatter: (entry: any) => `${entry.percent ? (entry.percent * 100).toFixed(1) : 0}%`
              }}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={[
                    '#0088FE', '#00C49F', '#FFBB28', '#FF8042'
                  ][index % 4]} 
                  strokeWidth={1}
                />
              ))}
            </Pie>
            <ChartTooltip 
              formatter={(value, name, props) => [
                `${costService.formatCurrency(value as number)}`, 
                props.payload.name
              ]}
              contentStyle={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px'
              }}
            />
            <Legend 
              layout="horizontal" 
              verticalAlign="bottom" 
              align="center"
              iconType="circle"
              iconSize={10}
              wrapperStyle={{ 
                paddingTop: '20px',
                bottom: 0,
                marginTop: '10px'
              }}
              // @ts-ignore - Propriedades do Recharts não estão corretamente tipadas
              formatter={(value: string, entry: any) => {
                const item = chartData.find(item => item.name === value);
                return (
                  <span style={{ 
                    color: '#333', 
                    fontSize: '0.8rem', 
                    marginRight: '15px',
                    fontWeight: 500,
                    display: 'inline-block',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(0,0,0,0.03)'
                  }}>
                    {value}: {item ? `${costService.formatCurrency(item.value)}` : ''}
                  </span>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default CostPieChart;
